import { SizeInfoItem } from './types';

interface GetMySizeRowPropsParams {
  isMyPurchaseTab: boolean;
  myPurchaseData: any[] | undefined; // Replace 'any' with actual type if available
  selectedPurchaseId: string | null;
  userMeasureRow: string[] | undefined;
  purchaseCompareRow: string[] | undefined; // 추가된 prop
  isAgreedFinal: boolean;
  isSizeComparable: boolean;
  onCompareClick: () => void;
  highlightFlags?: boolean[][];
}

interface MySizeRowProps {
  label: string;
  actionLabel: string;
  onAction?: () => void;
  values?: string[];
  matchedFlags?: boolean[];
}

/**
 * 상단 고정 행(MySizeRow)에 들어갈 데이터를 결정하는 헬퍼 함수입니다.
 *
 * 요구사항:
 * 1. 구매 내역 탭: 선택된 구매 상품이 있으면 해당 정보를 '비교상품'으로 노출. 없으면 안내 문구 노출.
 * 2. 내 사이즈 탭:
 *    - 내 사이즈 정보가 있고 동의했다면, 수치 데이터를 노출.
 *    - 데이터가 없으면 '사이즈 입력하고 비교하기' 버튼 노출.
 *    - 비교 불가능한 상품이면(isSizeComparable=false) 아무것도 노출하지 않음.
 *
 * @returns MySizeRow 컴포넌트에 전달할 props 객체 또는 undefined (렌더링 안 함)
 */
export const getMySizeRowProps = ({
  isMyPurchaseTab,
  myPurchaseData,
  selectedPurchaseId,
  userMeasureRow,
  purchaseCompareRow, // 추가된 prop
  isAgreedFinal,
  isSizeComparable,
  onCompareClick,
  highlightFlags,
}: GetMySizeRowPropsParams): MySizeRowProps | undefined => {
  const getMatchedFlags = (values: string[]) => {
    if (!highlightFlags || highlightFlags.length === 0 || !values) {
      return undefined;
    }
    return values.map((_, i) => {
      // highlightFlags의 각 row의 (i+1)번째 컬럼 중 하나라도 true면 true
      // (0번째 컬럼은 라벨이므로 데이터는 1부터 시작)
      return highlightFlags.some((rowFlags) => rowFlags[i + 1]);
    });
  };

  // 1. 내 구매 내역 탭일 때의 로직
  if (isMyPurchaseTab) {
    if (!myPurchaseData || myPurchaseData.length === 0) {
      return {
        label: '비교상품',
        actionLabel: '비교할 구매내역이 없어요',
        onAction: undefined,
      };
    }

    if (!selectedPurchaseId) {
      return {
        label: '비교상품',
        actionLabel: '비교할 상품을 선택해 주세요',
        onAction: undefined,
      };
    }

    // 선택된 구매 내역이 있으면 해당 데이터를 MySizeRow로 반환
    if (purchaseCompareRow) {
      const values = purchaseCompareRow.slice(1);
      return {
        label: '비교상품',
        actionLabel: '',
        values, // 라벨 제외하고 값만 전달
        matchedFlags: getMatchedFlags(values),
      };
    }

    return undefined;
  }

  // 2. 내 사이즈(신체 치수) 비교 탭일 때의 로직

  if (userMeasureRow && isAgreedFinal) {
    // values 배열의 첫 번째 요소는 라벨("내 사이즈")이므로 제외하고 실제 값들만 추출
    // MySizeRow 컴포넌트에서는 values가 있으면 값들을 렌더링하고, 없으면 Action 버튼을 렌더링함
    const values = userMeasureRow.slice(1);

    return {
      label: '내 사이즈',
      actionLabel: '',
      onAction: onCompareClick,
      values: values,
      matchedFlags: getMatchedFlags(values),
    };
  }

  if (!isSizeComparable) {
    return undefined;
  }

  // 3. 사이즈 비교 가능하지만 아직 입력/동의 안 된 상태 -> 입력 유도
  return {
    label: '내 사이즈',
    actionLabel: '사이즈 입력하고 비교하기',
    onAction: onCompareClick,
    // values는 위에서 undefined로 리턴되므로 여기서는 항상 undefined일 것임.
    // 하지만 원본 코드의 마지막 줄 삼항 연산자를 그대로 살리자면:
    values: undefined,
  };
};
